import { useEffect, useMemo, useState } from 'react';
import { legal as Api, type LegalPage } from '@tvx/api-client';
import { useAsync } from '../lib/useAsync';
import { Shim } from '../components/Shimmer';
import { Field, Row } from '../components/Field';
import { SeoPanel } from '../components/SeoPanel';
import { parseStamp } from '../lib/time';

/**
 * Terms, privacy and refunds.
 *
 * Long prose rather than fields, so the editor is a big textarea and the work
 * this screen does is around it: showing which {tokens} are available, saying
 * when the page last changed, and keeping a way back to the shipped draft —
 * there is no undo on a textarea and these are long documents.
 */
export function Legal() {
  const { data, error, loading, reload } = useAsync((signal) => Api.load(signal));
  const [slug, setSlug] = useState('terms');
  const [draft, setDraft] = useState<LegalPage | null>(null);
  const [status, setStatus] = useState('');
  const [dirty, setDirty] = useState(false);

  const page = useMemo(
    () => data?.pages.find((p) => p.slug === slug) ?? data?.pages[0],
    [data, slug],
  );

  useEffect(() => { if (page) { setDraft({ ...page }); setDirty(false); } }, [page?.slug, page?.updated_at]);

  /* Leaving with unsaved text is the one way this editor could lose work. */
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  if (error) return <div className="err">{error.message}</div>;
  if (loading || !data || !draft) return <div style={{ display: 'grid', gap: 12 }}><Shim w="46%" h={16} /><Shim w="78%" /><Shim w="62%" /></div>;

  const set = (k: keyof LegalPage, v: string) => {
    setDraft({ ...draft, [k]: v } as LegalPage);
    setDirty(true);
  };

  async function save() {
    if (!draft) return;
    setStatus('Saving…');
    try {
      await Api.save(draft.slug, draft);
      setStatus('Saved · live on the site');
      setDirty(false);
      reload();
    } catch (e) {
      setStatus((e as Error).message);
    }
  }

  async function restore() {
    if (!draft) return;
    if (!window.confirm('Replace this page with the draft Tradevix ships? Your edits are lost.')) return;
    await Api.restore(draft.slug);
    setStatus('The shipped draft is back');
    reload();
  }

  return (
    <>
      <div className="rowbar">
        <div>
          <h1 style={{ fontSize: 27, margin: 0 }}>Legal pages</h1>
          <p className="muted" style={{ fontSize: 13.5, marginTop: 6 }}>
            Terms, privacy and refunds. A payment provider will ask to see all three before it
            onboards you — and they are the pages a customer reads when something has gone wrong.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <a className="btn2 bo" href={data.routes[draft.slug]} target="_blank" rel="noreferrer"
            style={{ textDecoration: 'none' }}>View page ↗</a>
          <button className="btn2 bg" onClick={save} disabled={!dirty}>Save</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {data.pages.map((p) => (
          <button
            key={p.slug}
            className={`btn2 ${p.slug === draft.slug ? 'bg' : 'bo'}`}
            onClick={() => {
              if (dirty && !window.confirm('Leave this page? Unsaved text is lost.')) return;
              setSlug(p.slug);
            }}
          >
            {p.title}{p.state === 'draft' ? ' · hidden' : ''}
          </button>
        ))}
      </div>

      <div className="legal-split" style={{ display: 'grid', gap: 18, alignItems: 'start' }}>
        <div className="crd" style={{ padding: '20px 22px' }}>
          <Row>
            <Field label="Page title"><input className="inp" value={draft.title}
              onChange={(e) => set('title', e.target.value)} /></Field>
            <Field label="Shown on the site" hint="Hidden pages 404 and leave the footer.">
              <select className="inp" value={draft.state} onChange={(e) => set('state', e.target.value)}>
                <option value="published">Published</option>
                <option value="draft">Hidden</option>
              </select>
            </Field>
          </Row>

          <Field label="The page"
            hint="Blank line between paragraphs. A line starting with ## is a heading. **bold** works.">
            <textarea className="inp ta" style={{ minHeight: 520, fontFamily: 'ui-monospace, monospace', fontSize: 12.5, lineHeight: 1.6 }}
              value={draft.body} onChange={(e) => set('body', e.target.value)} />
          </Field>

          <p className="muted" style={{ fontSize: 11.5 }}>
            Last updated {parseStamp(draft.updated_at)?.toLocaleDateString('en-IN',
              { day: '2-digit', month: 'short', year: 'numeric' }) ?? '—'} — the date shown on the page.
          </p>
        </div>

        <div style={{ display: 'grid', gap: 16 }}>
          <SeoPanel
            url={`https://tradevix.in${data.routes[draft.slug] ?? '/'}`}
            seo={{
              title: draft.seo_title,
              description: draft.seo_description,
              robots: draft.state === 'draft' ? 'noindex' : '',
              builtin_title: `${draft.title} — Tradevix`,
              builtin_description: '',
              forced_noindex: draft.state === 'draft',
            }}
            value={{ title: draft.seo_title, description: draft.seo_description,
              robots: draft.state === 'draft' ? 'noindex' : '' }}
            onChange={(next) => {
              setDraft({ ...draft, seo_title: next.title, seo_description: next.description });
              setDirty(true);
            }}
          />

          <div className="crd" style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase',
              color: 'var(--tx3)', fontWeight: 600, marginBottom: 10 }}>Tokens you can use</div>
            <p className="muted" style={{ fontSize: 11.5, marginBottom: 12, lineHeight: 1.55 }}>
              Filled in when the page is served, from Settings. Use them instead of typing the
              address or the GSTIN — then changing it in one place changes it everywhere.
            </p>
            {data.tokens.map((t) => (
              <div key={t.token} style={{ display: 'flex', gap: 8, marginTop: 7, fontSize: 11.5 }}>
                <code style={{ color: 'var(--goldtx)' }}>{t.token}</code>
                <span className="muted" style={{ flex: 1 }}>{t.note}</span>
              </div>
            ))}
          </div>

          <div className="crd" style={{ padding: '16px 18px' }}>
            <div style={{ fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase',
              color: 'var(--tx3)', fontWeight: 600, marginBottom: 10 }}>Start again</div>
            <p className="muted" style={{ fontSize: 11.5, marginBottom: 12, lineHeight: 1.55 }}>
              Puts back the draft this console ships with. Your edits to this page are lost.
            </p>
            <button className="btn2 bo" style={{ width: '100%' }} onClick={restore}>Restore the shipped draft</button>
          </div>

          <div className="crd" style={{ padding: '16px 18px', borderColor: 'rgba(var(--goldrgb), .35)' }}>
            <div style={{ fontSize: 10, letterSpacing: '.12em', textTransform: 'uppercase',
              color: 'var(--goldtx)', fontWeight: 600, marginBottom: 10 }}>Worth saying once</div>
            <p className="muted" style={{ fontSize: 11.5, lineHeight: 1.55 }}>
              These drafts were written around what this business does, but they are not legal
              advice. Have a professional read them before you rely on them — particularly the
              part about what Tradevix is and is not registered to do.
            </p>
          </div>
        </div>
      </div>

      {status ? <p className="muted" style={{ fontSize: 12.5, marginTop: 14 }}>{status}</p> : null}
    </>
  );
}
